import { forwardRef } from 'react';
import { Disableable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { useId } from '@optimus-web/helpers';
import { VisuallyHidden } from '../helpers';
import { ChipContainer, ChipIconButton, ChipText } from './style';

export type ChipValue = string | number | boolean;
export interface ChipProps extends Disableable {
    children: React.ReactNode;
    value?: ChipValue;
    onRemove?: (event: React.SyntheticEvent, value: ChipValue) => void;
    error?: boolean;
    cancelEvent?: boolean;
    showRemoveButton?: boolean;
    readOnly?: boolean;
    errorId?: string;
}

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
    (
        {
            children,
            value,
            onRemove,
            disabled = false,
            error = false,
            cancelEvent = true,
            showRemoveButton = true,
            readOnly = false,
            errorId,
            ...other
        },
        ref
    ) => {
        const { t } = useLocalization();
        const id = useId('chip-remove-button');

        const handleRemoveButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            if (cancelEvent) {
                event.stopPropagation();
            }
            onRemove?.(event, value ?? '');
        };

        return (
            <ChipContainer
                ref={ref}
                disabled={disabled}
                error={error}
                showRemoveButton={showRemoveButton}
                readOnly={readOnly}
                aria-invalid={error}
                aria-labelledby={errorId || undefined}
                {...other}
            >
                <ChipText data-test-chip-text disabled={disabled} error={error} readOnly={readOnly}>
                    {children}
                </ChipText>
                {showRemoveButton && !readOnly ? (
                    <>
                        <ChipIconButton
                            icon={IconName.CrossClose}
                            onClick={handleRemoveButtonClick}
                            size="small"
                            data-test-button-remove
                            disabled={disabled}
                            error={error}
                            aria-labelledby={id}
                        />
                        <VisuallyHidden aria-hidden id={id}>
                            {children} {t(OptimusTextKey.Remove)}
                        </VisuallyHidden>
                    </>
                ) : null}
            </ChipContainer>
        );
    }
);
